import { z } from "zod";
import { Id } from "../common/ids.js";
import { LocalizedText } from "../common/localized-text.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
import { AttributeDefinition, CategoryView, ListingKind, ProductStatus } from "./listing.js";
/** Categories and their attribute schemas (CAT-2, ADM-4, card 035). */
const Ops = { surface: "operator", auth: "operator", permission: "ops:catalog.manage" };
const CategoryId = z.object({ categoryId: Id });
const CategorySlug = z
    .string()
    .min(2)
    .max(60)
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "Lower-case letters, digits, and single hyphens");
export const CategoryStatus = z.enum(["active", "retired"]).meta({ id: "CategoryStatus" });
export const OpsCategory = CategoryView.extend({
    status: CategoryStatus,
    attributeSchemaVersion: z.number().int().nonnegative(),
    productCount: z.number().int().nonnegative().describe("Products in this category, in any state"),
    liveOnOlderSchema: z
        .number()
        .int()
        .nonnegative()
        .describe("Live products last checked against an older version of the schema; they meet it at their next edit"),
    version: z.number().int().positive(),
    createdAt: Timestamp,
    updatedAt: Timestamp,
}).meta({ id: "OpsCategory" });
export const listPublicCategories = defineRoute({
    id: "catalog.public.categories.list",
    method: "GET",
    path: "/v1/public/categories",
    surface: "public",
    auth: "none",
    permission: "none",
    marks: [],
    summary: "The active category tree as a flat list, with each leaf's attribute schema",
    prd: ["CAT-2", "WEB-4"],
    responses: { 200: { description: "Categories", schema: z.object({ items: z.array(CategoryView) }) } },
    errors: ["RATE_LIMITED"],
});
export const listOpsCategories = defineRoute({
    ...Ops,
    id: "ops.catalog.categories.list",
    method: "GET",
    path: "/v1/ops/categories",
    marks: [],
    summary: "Every category, retired ones included, with product counts",
    prd: ["CAT-2", "ADM-4"],
    responses: { 200: { description: "Categories", schema: z.object({ items: z.array(OpsCategory) }) } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED"],
});
export const createCategory = defineRoute({
    ...Ops,
    id: "ops.catalog.categories.create",
    method: "POST",
    path: "/v1/ops/categories",
    marks: [],
    summary: "Add a category. A top-level category sets whether its branch lists goods or services; a child takes its parent's",
    prd: ["CAT-2", "ADM-4"],
    request: {
        body: z.object({
            parentId: Id.nullable(),
            slug: CategorySlug,
            name: LocalizedText,
            listingKind: ListingKind.optional().describe("Required at the top level; a child takes its parent's"),
            isLeaf: z.boolean(),
            sortOrder: z.number().int().min(0).max(10_000).default(0),
        }),
    },
    responses: { 201: { description: "The category", schema: OpsCategory } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "VALIDATION_FAILED", "NOT_FOUND"],
});
export const updateCategory = defineRoute({
    ...Ops,
    id: "ops.catalog.categories.update",
    method: "PATCH",
    path: "/v1/ops/categories/{categoryId}",
    marks: ["if-match"],
    summary: "Rename, move within its branch, reorder, retire, or change whether it is a leaf (only while it has no products or children)",
    prd: ["CAT-2", "ADM-4"],
    request: {
        params: CategoryId,
        body: z.object({
            name: LocalizedText.optional(),
            parentId: Id.nullable().optional(),
            sortOrder: z.number().int().min(0).max(10_000).optional(),
            isLeaf: z.boolean().optional(),
            status: CategoryStatus.optional(),
        }),
    },
    responses: { 200: { description: "The category", schema: OpsCategory } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "VALIDATION_FAILED", "NOT_FOUND", "VERSION_CONFLICT"],
});
export const AttributeUsage = z
    .object({
    key: z.string(),
    products: z
        .number()
        .int()
        .nonnegative()
        .describe("Products holding a value, in any status, edits included"),
    byStatus: z.array(z.object({ status: ProductStatus, products: z.number().int().positive() })),
    options: z
        .array(z.object({ value: z.string(), products: z.number().int().positive() }))
        .describe("For a choice, the products holding each value"),
})
    .meta({ id: "AttributeUsage" });
export const getAttributeUsage = defineRoute({
    ...Ops,
    id: "ops.catalog.categories.attribute_usage.get",
    method: "GET",
    path: "/v1/ops/categories/{categoryId}/attribute-usage",
    marks: [],
    summary: "Which attribute keys and choice values the category's products hold, counting pending edits of published products (card 048)",
    prd: ["CAT-2", "ADM-4"],
    request: { params: CategoryId },
    responses: { 200: { description: "Usage per key", schema: z.object({ items: z.array(AttributeUsage) }) } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const putAttributeSchema = defineRoute({
    ...Ops,
    id: "ops.catalog.categories.attribute_schema.put",
    method: "PUT",
    path: "/v1/ops/categories/{categoryId}/attribute-schema",
    marks: ["if-match"],
    summary: "Replace a leaf's attributes as a new schema version; live products keep the version they were checked against until their next edit. Dropping a key or choice value that products hold needs it in `confirmRemoval` (`KEY_IN_USE`, `OPTION_IN_USE`); an attribute in use keeps its type and whether it takes several values (`TYPE_IN_USE`)",
    prd: ["CAT-2", "ADM-4"],
    request: {
        params: CategoryId,
        body: z.object({
            attributes: z.array(AttributeDefinition).max(40),
            confirmRemoval: z
                .array(z.string().regex(/^[a-z][a-z0-9_]{1,39}(\.[a-z][a-z0-9_]{1,39})?$/))
                .max(200)
                .default([])
                .describe("Keys (`grade`) and choice values (`finish.gloss`) in use that the operator agreed to drop"),
        }),
    },
    responses: { 200: { description: "The category with its new schema", schema: OpsCategory } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "VALIDATION_FAILED", "NOT_FOUND", "VERSION_CONFLICT"],
});
export const categoryRoutes = [
    listPublicCategories,
    listOpsCategories,
    createCategory,
    updateCategory,
    getAttributeUsage,
    putAttributeSchema,
];
//# sourceMappingURL=category.routes.js.map